<template>
  <div class="plano-card">
    <!-- Header row -->
    <div class="plano-card-header">
      <button type="button" class="plano-card-toggle" @click="toggle">
        <div class="plano-icon-wrap">
          <font-awesome-icon :icon="['fas', 'clipboard-list']" class="plano-icon" />
        </div>
        <div class="plano-card-title-area">
          <span class="plano-level-label">AÇÃO</span>
          <p class="plano-card-name">{{ plan.name }}</p>
        </div>
        <font-awesome-icon
          :icon="['fas', isOpen ? 'chevron-down' : 'chevron-right']"
          class="plano-chevron"
        />
      </button>
      <div class="plano-card-actions">
        <button
          v-if="canAddAction && plan.id && $can('action_plan:create')"
          type="button"
          class="plano-action-btn plano-action-btn-success"
          title="Adicionar Ação"
          @click.stop="openAddActionItem"
        >
          <font-awesome-icon :icon="['fas', 'plus']" />
        </button>
        <button
          v-if="plan.id && $can('objective:view')"
          type="button"
          class="plano-action-btn"
          title="Visualizar ação"
          @click.stop="openShowPlanAction"
        >
          <font-awesome-icon :icon="['far', 'eye']" />
        </button>
        <button
          v-if="plan.id && $can('objective:edit')"
          type="button"
          class="plano-action-btn"
          title="Editar"
          @click.stop="openEditAction"
        >
          <font-awesome-icon :icon="['fas', 'pencil-alt']" />
        </button>
        <button
          v-if="plan.id && $can('objective:delete')"
          type="button"
          class="plano-action-btn plano-action-btn-danger"
          title="Excluir"
          @click.stop="confirmDelete"
        >
          <font-awesome-icon :icon="['fas', 'trash']" />
        </button>
      </div>
    </div>

    <!-- Collapsed badges: progress bar + status pills -->
    <div class="plano-collapsed-badges">
      <div class="progress-bar-mini-wrap">
        <div class="progress-bar-mini">
          <div class="progress-bar-fill" :class="completudeBarClass" :style="{ width: completudePct + '%' }" />
        </div>
        <span class="progress-bar-label" :class="completudeTextClass">{{ completudeTexto }}</span>
      </div>
      <span class="status-badge" :class="orcamentoStatusClass">
        <font-awesome-icon :icon="['fas', 'dollar-sign']" class="status-badge-icon" />
        {{ orcamentoBadgeTexto }}
      </span>
      <span class="status-badge" :class="cronogramaStatusClass">
        <font-awesome-icon :icon="['fas', 'calendar']" class="status-badge-icon" />
        {{ cronogramaBadgeTexto }}
      </span>
    </div>

    <!-- Expanded content -->
    <b-collapse v-model="isOpen">
      <div class="plano-expanded animate-slide-in">
        <!-- Overview grid: 3 columns -->
        <div class="row overview-grid mb-3">
          <div class="col-sm-4 mb-2 mb-sm-0">
            <div class="overview-box">
              <div class="overview-box-label">
                <font-awesome-icon :icon="['fas', 'check-square']" class="mr-1" /> Progresso
              </div>
              <div class="d-flex align-items-center mt-2">
                <div class="progress-bar-track">
                  <div class="progress-bar-fill" :class="completudeBarClass" :style="{ width: completudePct + '%' }" />
                </div>
                <span class="progress-label ml-2" :class="completudeTextClass">{{ completudeTexto }}</span>
              </div>
            </div>
          </div>
          <div class="col-sm-4 mb-2 mb-sm-0">
            <div class="overview-box">
              <div class="overview-box-label">
                <font-awesome-icon :icon="['fas', 'dollar-sign']" class="mr-1" /> Orçamento
              </div>
              <p class="overview-value mt-1">{{ orcamentoValores }}</p>
              <span class="status-badge overview-status" :class="orcamentoStatusClass">
                <font-awesome-icon :icon="['fas', 'dollar-sign']" class="status-badge-icon" />
                {{ orcamentoBadgeTexto }}
              </span>
            </div>
          </div>
          <div class="col-sm-4 mb-2 mb-sm-0">
            <div class="overview-box">
              <div class="overview-box-label">
                <font-awesome-icon :icon="['fas', 'calendar']" class="mr-1" /> Cronograma
              </div>
              <span class="status-badge mt-2" :class="cronogramaStatusClass">
                <font-awesome-icon :icon="['fas', 'calendar']" class="status-badge-icon" />
                {{ cronogramaTextoCompleto }}
              </span>
            </div>
          </div>
        </div>

        <!-- Actions section -->
        <div class="acoes-section mb-3">
          <div class="acoes-header">
            <span class="acoes-header-label">
              <font-awesome-icon :icon="['fas', 'check-square']" class="mr-1" /> Subações
            </span>
          </div>
          <div class="acoes-list">
            <div
              v-for="(acao, idx) in acoesPlaceholder"
              :key="acao.id || idx"
              class="acao-item"
            >
              <span class="acao-nome">{{ acao.what || acao.name || 'Ação ' + (idx + 1) }}</span>
              <div class="acao-item-right">
                <span
                  v-if="acaoCronogramaClass(acao) !== 'status-danger'"
                  class="status-badge"
                  :class="acaoStatusClass(acao)"
                >
                  {{ acao.statusLabel }}
                </span>
                <span class="status-badge subacao-cronograma-badge" :class="acaoCronogramaClass(acao)">
                  <font-awesome-icon :icon="['fas', 'calendar']" class="status-badge-icon" />
                  <span class="subacao-cronograma-text">{{ acaoCronogramaTexto(acao) }}</span>
                </span>
                <button
                  v-if="acao.id"
                  type="button"
                  class="plano-action-btn"
                  title="Visualizar"
                  @click.stop="openShowAction(acao)"
                >
                  <font-awesome-icon :icon="['far', 'eye']" />
                </button>
                <button
                  v-if="acao.id && (acao.action_plan_id || plan.id)"
                  type="button"
                  class="plano-action-btn"
                  title="Editar ação"
                  @click.stop="openEditActionItem(acao)"
                >
                  <font-awesome-icon :icon="['fas', 'pencil-alt']" />
                </button>
                <button
                  v-if="acao.id && $can('objective:delete')"
                  type="button"
                  class="plano-action-btn plano-action-btn-danger"
                  title="Excluir subação"
                  @click.stop="confirmDeleteSubacao(acao)"
                >
                  <font-awesome-icon :icon="['fas', 'trash']" />
                </button>
              </div>
            </div>
            <p v-if="!acoesPlaceholder.length" class="acoes-empty">Nenhuma ação cadastrada.</p>
          </div>
        </div>

        <!-- Metas / Indicadores -->
        <div v-if="hasMetas || hasIndicadores" class="row metas-indicadores-grid">
          <div v-if="hasMetas" class="col-sm-6 mb-2 mb-sm-0">
            <div class="meta-box">
              <span class="meta-box-label">
                <font-awesome-icon :icon="['fas', 'check-square']" class="mr-1" /> Metas
              </span>
              <div
                v-for="(m, i) in (objective.measures || []).slice(0, 5)"
                :key="m.id || i"
                class="meta-row"
              >
                <span>{{ m.name || m.title || 'Meta ' + (i + 1) }}</span>
                <span v-if="m.value != null" class="meta-value">{{ m.value }}</span>
              </div>
            </div>
          </div>
          <div v-if="hasIndicadores" class="col-sm-6 mb-2 mb-sm-0">
            <div class="meta-box">
              <span class="meta-box-label">
                <font-awesome-icon :icon="['fas', 'chart-bar']" class="mr-1" /> Indicadores
              </span>
              <div
                v-for="(ind, i) in (objective.indicators || []).slice(0, 5)"
                :key="ind.id || i"
                class="meta-row"
              >
                <span>{{ ind.name || ind.title || 'Indicador ' + (i + 1) }}</span>
                <span class="meta-value">
                  <strong>{{ ind.value || '–' }}</strong>
                  <span class="meta-value-sep"> / meta {{ ind.meta || '–' }}</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-collapse>

    <ModalAddActionItem
      ref="modalAddActionItem"
      @saved="onActionItemCreated"
    />

    <ModalEditActionItem
      ref="modalEditActionItem"
      @saved="onActionItemEdited"
    />

    <ModalShowAction ref="modalShowAction" />
  </div>
</template>

<script>
import format_date from '@/helpers/filters/format_date';
import services from '@/http';
import ModalAddActionItem from './ModalAddActionItem.vue';
import ModalEditActionItem from './ModalEditActionItem.vue';
import ModalShowAction from '@/modules/action_plans/components/ModalShowAction.vue';

export default {
  name: 'PlanoAcaoCard',
  components: { ModalAddActionItem, ModalEditActionItem, ModalShowAction },
  inject: {
    reloadTree: { default: () => () => {} },
  },
  props: {
    objective: { type: Object, required: true },
    plan: { type: Object, required: true },
    strategicPlanId: { type: [Number, String], required: true },
    expandAll: { type: Boolean, default: false },
    canAddAction: { type: Boolean, default: false },
  },
  data() {
    return {
      isOpen: this.expandAll,
    };
  },
  watch: {
    expandAll(v) {
      this.isOpen = v;
    },
  },
  computed: {
    actionPlanItems() {
      const items = this.plan.action_plan_item || this.plan.action_plan_items || this.plan.items || this.objective.action_plans_items || [];
      const arr = Array.isArray(items) ? items : [];
      return arr.filter((a) => a.parent_id != null);
    },
    /**
     * Progresso: com subações, conta só as filhas; sem subações, conta a ação raiz (ação única finalizada = 100%).
     */
    itemsForCompletude() {
      const arr = this.actionPlanItems;
      if (arr.length > 0) return arr;
      const root = this.rootActionItem;
      return root ? [root] : [];
    },
    completudeTotais() {
      const totalApi = Number(this.plan?.total_acoes);
      const finalizadasApi = Number(this.plan?.total_acoes_finalizadas);
      const apiLooksValid =
        !Number.isNaN(totalApi) &&
        !Number.isNaN(finalizadasApi) &&
        (totalApi > 0 || finalizadasApi > 0);
      if (apiLooksValid) {
        return { total: totalApi, done: finalizadasApi };
      }

      const items = this.itemsForCompletude;
      const total = items.length;
      const done = items.filter((i) => {
        const s = String(i.prazo?.status || i.status || '').toLowerCase();
        return s.includes('conclu') || s.includes('finished');
      }).length;
      return { total, done };
    },
    completudePct() {
      const { total, done } = this.completudeTotais;
      return total > 0 ? Math.round((done / total) * 100) : 0;
    },
    completudeTexto() {
      const { total, done } = this.completudeTotais;
      const pct = total > 0 ? Math.round((done / total) * 100) : 0;
      return done + '/' + total + ' (' + pct + '%)';
    },
    completudeBarClass() {
      const { total, done } = this.completudeTotais;
      if (total === 0 || done === 0) return 'bar-neutral';
      const pct = this.completudePct;
      if (pct >= 100) return 'bar-success';
      return 'bar-completude-progress';
    },
    completudeTextClass() {
      const { total, done } = this.completudeTotais;
      if (total === 0 || done === 0) return 'completude-label-neutral';
      const pct = this.completudePct;
      if (pct >= 100) return 'completude-label-success';
      return 'completude-label-progress';
    },
    orcamentoStatusFromApi() {
      return this.plan?.orcamento?.status || '';
    },
    orcamentoStatusClass() {
      const s = this.orcamentoStatusFromApi.toLowerCase();
      if (s.includes('excedeu')) return 'status-danger';
      if (s.includes('dentro')) return 'status-success';
      return 'status-neutral';
    },
    orcamentoBadgeTexto() {
      return this.orcamentoStatusFromApi || 'Sem orçamento';
    },
    orcamentoValores() {
      if (!this.orcamentoStatusFromApi || this.orcamentoStatusFromApi.toLowerCase().includes('sem')) return '–';
      const orc = this.plan?.orcamento;
      if (orc && (orc.total_budget > 0 || orc.total_realized > 0)) {
        const budget = Number(orc.total_budget) || 0;
        const realized = Number(orc.total_realized) || 0;
        if (budget > 0) {
          return `R$ ${realized.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} / R$ ${budget.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
        }
        return `R$ ${realized.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
      }
      return this.plan.budget || this.formatBudgetFromItems();
    },
    /** Atraso do cronograma só da ação pai (item raiz / plano); subações atrasadas não alteram este estado. */
    cronogramaAtrasado() {
      return this.cronogramaAtrasadosCount > 0;
    },
    cronogramaAtrasadosCount() {
      const root = this.rootActionItem;
      if (root) {
        if (this.isDelayedStatus(root.prazo?.status || root.status)) return 1;
        if (this.isItemScheduleLateByDate(root)) return 1;
        return 0;
      }
      if (this.isDelayedStatus(this.plan?.prazo?.status)) return 1;
      if (this.isPlanLevelPastDueByDate()) return 1;
      return 0;
    },
    cronogramaStatusClass() {
      if (this.cronogramaAtrasado) return 'status-danger';
      if (!this.cronogramaDataFimFormatada) return 'status-neutral';
      return 'status-success';
    },
    cronogramaBadgeTexto() {
      if (this.cronogramaAtrasado) return 'Atrasado';
      if (!this.cronogramaDataFimFormatada) return 'Sem cronograma';
      return 'No prazo';
    },
    /** Prazo exibido no card: prioriza a ação pai (raiz), não o agregado das subações. */
    cronogramaDataFimFormatada() {
      const root = this.rootActionItem;
      if (root?.prazo?.data_termino) return format_date(root.prazo.data_termino);
      if (root?.when) return format_date(root.when);
      if (this.plan?.prazo?.data_termino) return format_date(this.plan.prazo.data_termino);
      if (this.plan?.deadline) return format_date(this.plan.deadline);
      return null;
    },
    cronogramaTextoCompleto() {
      const fim = this.cronogramaDataFimFormatada;
      if (!fim) {
        if (this.cronogramaAtrasado) return 'Atrasado';
        return 'Sem cronograma';
      }
      return this.cronogramaAtrasado
        ? `Atrasado – ${fim}`
        : `No prazo - até ${fim}`;
    },
    hasMetas() {
      return (this.objective.measures || []).length > 0;
    },
    hasIndicadores() {
      return (this.objective.indicators || []).length > 0;
    },
    rootActionItem() {
      const items = this.plan.action_plan_item || this.plan.action_plan_items || this.plan.items || this.objective?.action_plans_items || [];
      const arr = Array.isArray(items) ? items : [];
      return arr.find((a) => a.parent_id == null || a.parent_id === undefined) || null;
    },
    acoesPlaceholder() {
      const items = this.actionPlanItems;
      if (items.length === 0) return [];
      return items.map((a) => {
        const s = String(a.prazo?.status || a.status || '').toLowerCase();
        let label = 'Aguardando';
        if (s.indexOf('atrasad') !== -1 || s.indexOf('delayed') !== -1) label = 'Atrasada';
        else if (s.indexOf('conclu') !== -1 || s.indexOf('finished') !== -1) label = 'Finalizado';
        else if (s.indexOf('cancel') !== -1 || s.indexOf('cancelled') !== -1) label = 'Cancelado';
        else if (s.indexOf('paraliz') !== -1 || s.indexOf('stopped') !== -1) label = 'Paralizado';
        else if (s.indexOf('planned') !== -1 || s.indexOf('planejad') !== -1 || s.indexOf('não iniciada') !== -1) label = 'Planejado';
        else if (s.indexOf('no prazo') !== -1 || s.indexOf('on time') !== -1 || s.indexOf('ontime') !== -1 || s.indexOf('andamento') !== -1 || s.indexOf('progress') !== -1) label = 'Em progresso';
        const prazoInicio = a.prazo?.data_inicio ? format_date(a.prazo.data_inicio) : '';
        const prazoFim = a.prazo?.data_termino ? format_date(a.prazo.data_termino) : '';
        const whenFormatted = [prazoInicio, prazoFim].filter(Boolean).join(' a ')
          || (a.when ? format_date(a.when) : (a.when_formatted || (a.when_start ? format_date(a.when_start) : '–')));
        return {
          ...a,
          what: a.what || a.name,
          statusLabel: label,
          whenFormatted,
        };
      });
    },
  },
  methods: {
    async confirmDelete() {
      const id = this.plan?.id;
      if (!id) return;

      const value = await this.$bvModal.msgBoxConfirm('Essa operação não poderá ser desfeita.', {
        title: 'Deseja realmente excluir?',
        okVariant: 'danger',
        okTitle: 'SIM',
        size: 'md',
        cancelVariant: 'outline-primary',
        cancelTitle: 'NÃO',
        centered: true,
      });

      if (!value) return;

      try {
        await services.strategic_plans.plan_structure_delete_action({
          planStructureId: this.plan.plan_structure_id,
          actionId: id,
        });
        this.$notifications.addNotification({
          message: 'Ação excluída com sucesso',
          type: 'success',
        });
      } catch (e) {
        this.$notifications.addNotification({
          message: 'Erro ao excluir ação',
          type: 'danger',
        });
      }
      this.reloadTree();
    },
    async confirmDeleteSubacao(acao) {
      if (!acao?.id) return;

      const value = await this.$bvModal.msgBoxConfirm('Essa operação não poderá ser desfeita.', {
        title: 'Excluir esta subação?',
        okVariant: 'danger',
        okTitle: 'SIM',
        size: 'md',
        cancelVariant: 'outline-primary',
        cancelTitle: 'NÃO',
        centered: true,
      });

      if (!value) return;

      try {
        await services.action_plans.delete_plan_items({ id: acao.id });
        this.$notifications.addNotification({
          message: 'Subação excluída com sucesso',
          type: 'success',
        });
      } catch (e) {
        this.$notifications.addNotification({
          message: 'Erro ao excluir subação',
          type: 'danger',
        });
      }
      this.reloadTree();
    },
    isDelayedStatus(status) {
      const s = String(status || '').toLowerCase();
      return s.includes('atras') || s.includes('delay');
    },
    isItemScheduleLateByDate(item) {
      const s = String(item?.prazo?.status || item?.status || '').toLowerCase();
      if (s.includes('conclu') || s.includes('finished') || s.includes('cancel')) return false;
      const endStr = item?.prazo?.data_termino || item?.when;
      if (!endStr) return false;
      const d = new Date(endStr);
      if (!Number.isFinite(d.getTime())) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      d.setHours(0, 0, 0, 0);
      return d.getTime() < today.getTime();
    },
    isPlanLevelPastDueByDate() {
      const s = String(this.plan?.prazo?.status || '').toLowerCase();
      if (s.includes('conclu') || s.includes('finished') || s.includes('cancel')) return false;
      const endStr = this.plan?.prazo?.data_termino;
      if (!endStr) return false;
      const d = new Date(endStr);
      if (!Number.isFinite(d.getTime())) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      d.setHours(0, 0, 0, 0);
      return d.getTime() < today.getTime();
    },
    toggle() {
      this.isOpen = !this.isOpen;
    },
    openAddActionItem() {
      this.$refs.modalAddActionItem.show(this.plan.id, this.objective?.id || this.plan?.plan_structure_id);
    },
    openEditAction() {
      this.$refs.modalEditActionItem.show(this.plan);
    },
    openShowPlanAction() {
      const rootItem = this.rootActionItem;
      const acao = rootItem || { id: this.plan.id, action_plan_id: this.plan.id };
      this.openShowAction(acao);
    },
    openShowAction(acao) {
      if (!acao?.id) return;
      const psId = this.objective?.id || this.plan?.plan_structure_id;
      this.$refs.modalShowAction.show({
        itemId: acao.id,
        planStructureId: psId || undefined,
        actionPlanId: acao.action_plan_id || this.plan?.id || undefined,
      });
    },
    openEditActionItem(acao) {
      this.$refs.modalEditActionItem.show(acao);
    },
    async onActionItemCreated() {
      this.$emit('action-item-created');
      await this.reloadTree();
    },
    onActionItemEdited() {
      this.reloadTree();
    },
    acaoStatusClass(acao) {
      const s = String(acao.prazo?.status || acao.status || '').toLowerCase();
      if (s.indexOf('atrasad') !== -1 || s.indexOf('delayed') !== -1) return 'status-danger';
      if (s.indexOf('cancel') !== -1 || s.indexOf('cancelled') !== -1) return 'status-neutral';
      if (s.indexOf('paraliz') !== -1 || s.indexOf('stopped') !== -1) return 'status-warning';
      if (s.indexOf('no prazo') !== -1 || s.indexOf('on time') !== -1 || s.indexOf('ontime') !== -1) return 'status-success';
      if (s.indexOf('conclu') !== -1 || s.indexOf('finished') !== -1) return 'status-success';
      if (s.indexOf('andamento') !== -1 || s.indexOf('progress') !== -1) return 'status-in-progress';
      if (s.indexOf('iniciad') !== -1 || s.indexOf('planned') !== -1 || s.indexOf('planejad') !== -1) return 'status-neutral';
      return 'status-neutral';
    },
    subacaoDataInicioFormatted(acao) {
      if (acao.prazo?.data_inicio) return format_date(acao.prazo.data_inicio);
      if (acao.when_start) return format_date(acao.when_start);
      return null;
    },
    subacaoDataFimFormatted(acao) {
      if (acao.prazo?.data_termino) return format_date(acao.prazo.data_termino);
      if (acao.when) return format_date(acao.when);
      return null;
    },
    /** Pill de cronograma na subação: mesmo padrão visual do cronograma da ação pai. */
    acaoCronogramaClass(acao) {
      const s = String(acao.prazo?.status || acao.status || '').toLowerCase();
      if (s.includes('conclu') || s.includes('finished')) return 'status-success';
      if (s.includes('cancel') || s.includes('cancelled')) return 'status-neutral';
      if (s.includes('paraliz') || s.includes('stopped')) return 'status-warning';
      if (this.isDelayedStatus(acao.prazo?.status || acao.status) || this.isItemScheduleLateByDate(acao)) {
        return 'status-danger';
      }
      const fim = this.subacaoDataFimFormatted(acao);
      const ini = this.subacaoDataInicioFormatted(acao);
      if (!fim && !ini) return 'status-neutral';
      return 'status-success';
    },
    acaoCronogramaTexto(acao) {
      const s = String(acao.prazo?.status || acao.status || '').toLowerCase();
      const fim = this.subacaoDataFimFormatted(acao);
      const ini = this.subacaoDataInicioFormatted(acao);

      if (s.includes('conclu') || s.includes('finished')) {
        return fim ? `Concluído – até ${fim}` : 'Concluído';
      }
      if (s.includes('cancel') || s.includes('cancelled')) {
        return 'Cancelado';
      }
      if (s.includes('paraliz') || s.includes('stopped')) {
        if (fim && ini) return `${ini} a ${fim}`;
        if (fim) return `Até ${fim}`;
        if (ini) return `A partir de ${ini}`;
        return 'Paralizado';
      }
      if (this.isDelayedStatus(acao.prazo?.status || acao.status) || this.isItemScheduleLateByDate(acao)) {
        return fim ? `Atrasado – ${fim}` : 'Atrasado';
      }
      if (fim) return `No prazo - até ${fim}`;
      if (ini) return `No prazo - a partir de ${ini}`;
      return 'Sem cronograma';
    },
    formatBudgetFromItems() {
      const items = this.actionPlanItems;
      let planned = 0;
      let executed = 0;
      for (const item of items) {
        const howMuch = item?.action_plans_how_much || item?.action_plan_how_much || [];
        if (Array.isArray(howMuch) && howMuch.length) {
          planned += howMuch.reduce((acc, it) => acc + Number(it?.budget || 0), 0);
          executed += howMuch.reduce((acc, it) => acc + Number(it?.realized || 0), 0);
        }
      }
      if (planned > 0) return `R$ ${executed.toLocaleString('pt-BR')} / R$ ${planned.toLocaleString('pt-BR')}`;
      return '–';
    },
  },
};
</script>

<style scoped>
/* ===== Card container ===== */
.plano-card {
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
  background: #fff;
  box-shadow: var(--shadow-card);
  overflow: hidden;
  margin-bottom: 0.5rem;
}

/* ===== Header ===== */
.plano-card-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
}
.plano-card-toggle {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  min-width: 0;
  background: none;
  border: none;
  padding: 0.25rem;
  margin: -0.25rem;
  border-radius: 0.5rem;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;
}
.plano-card-toggle:hover {
  background: rgba(0, 0, 0, 0.03);
}
.plano-icon-wrap {
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  background: rgba(51, 80, 128, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.plano-icon {
  font-size: 0.875rem;
  color: var(--level-plano-acao);
}
.plano-card-title-area {
  flex: 1;
  min-width: 0;
}
.plano-level-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--muted-foreground, #6b7280);
}
.plano-card-name {
  font-size: 0.875rem;
  font-weight: 600;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--foreground, #1f2937);
}
.plano-chevron {
  font-size: 0.875rem;
  color: #9ca3af;
  flex-shrink: 0;
}
.plano-card-actions {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex-shrink: 0;
}
.plano-action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 0.25rem;
  border: none;
  background: transparent;
  color: #9ca3af;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  text-decoration: none;
}
.plano-action-btn:hover {
  background: rgba(0, 0, 0, 0.06);
  color: #1f2937;
  text-decoration: none;
}
.plano-action-btn-danger:hover {
  background: rgba(var(--negative-rgb), 0.1);
  color: var(--negative);
}
.plano-action-btn-success:hover {
  background: rgba(25, 135, 84, 0.1);
  color: #198754;
}

/* ===== Collapsed badges ===== */
.plano-collapsed-badges {
  padding: 0 1rem 0.75rem;
  margin-top: -0.25rem;
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.progress-bar-mini-wrap {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1 1 12rem;
  min-width: 11rem;
  max-width: 14rem;
}
.progress-bar-mini {
  height: 0.5rem;
  flex: 1;
  border-radius: 9999px;
  background: rgba(0, 0, 0, 0.08);
  overflow: hidden;
  min-width: 5rem;
}
.progress-bar-fill {
  height: 100%;
  border-radius: 9999px;
  transition: width 0.5s ease;
}
.bar-success {
  background: var(--status-success);
}
.bar-completude-progress {
  background: #2563eb;
}
.bar-danger {
  background: var(--status-danger);
}
.bar-neutral {
  background: var(--status-neutral, #9ca3af);
}
.progress-bar-label {
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
}
.completude-label-neutral {
  color: var(--muted-foreground, #6b7280);
}
.completude-label-success {
  color: var(--status-success, #059669);
  font-weight: 600;
}
.completude-label-progress {
  color: #1d4ed8;
  font-weight: 600;
}
.completude-label-danger {
  color: var(--status-danger, #dc2626);
  font-weight: 600;
}

/* ===== Status badges ===== */
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.15rem 0.625rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  flex-shrink: 0;
}
.status-badge-icon {
  font-size: 0.75rem;
}
.status-success {
  background: var(--status-success-bg);
  color: var(--status-success);
}
.status-warning {
  background: var(--status-warning-bg);
  color: var(--status-warning);
}
.status-in-progress {
  background: #dbeafe;
  color: #1d4ed8;
}
.status-danger {
  background: var(--status-danger-bg);
  color: var(--status-danger);
}
.status-neutral {
  background: var(--status-neutral-bg);
  color: var(--status-neutral);
}

/* ===== Expanded content ===== */
.plano-expanded {
  border-top: 1px solid #e5e7eb;
  padding: 1rem;
}

/* Overview boxes */
.overview-box {
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 0.75rem;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}
.overview-box-label {
  display: inline-flex;
  align-items: center;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--muted-foreground, #6b7280);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
.overview-value {
  font-size: 0.875rem;
  font-weight: 600;
  margin: 0;
  color: var(--foreground, #1f2937);
}
.overview-status {
  margin-top: 0.25rem;
}
.overview-grid > [class*='col-'] {
  display: flex;
}

/* Progress bar in overview */
.progress-bar-track {
  height: 0.5rem;
  flex: 1;
  border-radius: 9999px;
  background: rgba(0, 0, 0, 0.08);
  overflow: hidden;
  min-width: 5rem;
}
.progress-label {
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
}

/* Acoes section */
.acoes-section {
}
.acoes-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}
.acoes-header-label {
  display: inline-flex;
  align-items: center;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--muted-foreground, #6b7280);
}
.acoes-list {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}
.acao-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 0.375rem;
  background: rgba(0, 0, 0, 0.02);
  transition: background 0.15s;
}
.acao-item:hover {
  background: rgba(0, 0, 0, 0.04);
}
.acao-nome {
  font-size: 0.875rem;
  font-weight: 400;
  color: var(--foreground, #1f2937);
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 0.5rem;
}
.acao-item-right {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  flex-shrink: 0;
}
.subacao-cronograma-badge {
  max-width: min(100%, 17rem);
  align-items: flex-start;
  text-align: left;
  line-height: 1.25;
}
.subacao-cronograma-text {
  white-space: normal;
  word-break: break-word;
}
/* Botões na lista de ações: ícone apenas, tamanho fixo */
.acoes-list .plano-action-btn {
  width: 1.75rem;
  min-width: 1.75rem;
  height: 1.75rem;
  padding: 0;
}
.acoes-empty {
  font-size: 0.875rem;
  color: #9ca3af;
  margin: 0;
  padding: 0.75rem 0;
}

/* Metas / Indicadores */
.metas-indicadores-grid {
  margin-top: 0.25rem;
}
.meta-box {
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 0.75rem;
}
.meta-box-label {
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--muted-foreground, #6b7280);
  display: inline-flex;
  align-items: center;
  margin-bottom: 0.5rem;
}
.meta-row {
  display: flex;
  justify-content: space-between;
  font-size: 0.875rem;
  padding: 0.25rem 0;
  color: var(--foreground, #1f2937);
}
.meta-value {
  font-weight: 600;
  color: var(--muted-foreground, #6b7280);
}
.meta-value-sep {
  color: #9ca3af;
}

@media (max-width: 575.98px) {
  .progress-bar-mini-wrap {
    flex-basis: 100%;
    max-width: none;
    min-width: 0;
  }
  .acao-item {
    flex-direction: column;
    align-items: stretch;
  }
  .acao-nome {
    margin-right: 0;
  }
  .acao-item-right {
    width: 100%;
    justify-content: flex-start;
  }
}
</style>
